import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import UserBadge from '../components/UserBadge';
import LoadingSpinner from '../components/LoadingSpinner';
import { apiRequest, UPLOADS_BASE_URL } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { getAvatarStyle, getInitials } from '../utils/avatarUtils';

interface PilotUser {
  _id: string;
  userid: string;
  name: string;
  organization?: string;
  logo?: string;
  userRole?: string;
}

interface Aircraft {
  _id: string;
  identification: string;
  type: string;
}

interface PublicFlight {
  _id: string;
  aircraftid: Aircraft | null;
  dateStart: string;
  duration: number;
  region?: string;
}

type FriendshipStatus = 'none' | 'pending_sent' | 'pending_received' | 'accepted' | 'self';

const AIRCRAFT_TYPE_LABELS: Record<string, string> = {
  balloon: 'Ballon',       ballon: 'Ballon',
  glider: 'Segelflugzeug', segelflugzeug: 'Segelflugzeug',
  plane: 'Motorflugzeug',  motorflugzeug: 'Motorflugzeug',
  helicopter: 'Hubschrauber', hubschrauber: 'Hubschrauber',
};

const ROLE_LABELS: Record<string, string> = {
  pilot: 'Pilot',
  flugschule: 'Flugschule',
};

function formatDate(s: string) {
  return new Date(s).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDuration(secs: number) {
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return `${h}:${m.toString().padStart(2, '0')} h`;
}

const PilotProfile: React.FC = () => {
  const { userid } = useParams<{ userid: string }>();
  const navigate = useNavigate();
  const { userDetails } = useAuth();

  const [pilot, setPilot] = useState<PilotUser | null>(null);
  const [flights, setFlights] = useState<PublicFlight[]>([]);
  const [friendStatus, setFriendStatus] = useState<{ status: FriendshipStatus; friendshipId?: string }>({ status: 'none' });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (!userid) return;
    const load = async () => {
      try {
        const [pilotRes, flightsRes, statusRes] = await Promise.all([
          apiRequest(`/users/authentik/${userid}`),
          apiRequest(`/flight-tracks/public?dateFrom=2000-01-01`),
          apiRequest(`/friends/status/${userid}`),
        ]);

        if (pilotRes.ok) setPilot(await pilotRes.json());

        if (flightsRes.ok) {
          const all: PublicFlight[] = await flightsRes.json();
          setFlights(all.filter(f => (f as any).userid === userid && f.aircraftid).slice(0, 20));
        }

        if (statusRes.ok) setFriendStatus(await statusRes.json());
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userid]);

  const handleFriendAction = async (action: 'request' | 'accept' | 'decline' | 'remove') => {
    setActionLoading(true);
    try {
      if (action === 'request') {
        await apiRequest(`/friends/request/${userid}`, { method: 'POST' });
        setFriendStatus({ status: 'pending_sent' });
      } else if (action === 'accept' && friendStatus.friendshipId) {
        await apiRequest(`/friends/requests/${friendStatus.friendshipId}/accept`, { method: 'PUT' });
        setFriendStatus({ status: 'accepted' });
      } else if (action === 'decline' && friendStatus.friendshipId) {
        await apiRequest(`/friends/requests/${friendStatus.friendshipId}/decline`, { method: 'PUT' });
        setFriendStatus({ status: 'none' });
      } else if (action === 'remove') {
        await apiRequest(`/friends/${userid}`, { method: 'DELETE' });
        setFriendStatus({ status: 'none' });
      }
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) return <div className="container"><LoadingSpinner message="Lade Profil..." fullPage /></div>;
  if (!pilot) return (
    <div className="container" style={{ padding: '40px' }}>
      <div className="alert alert-danger">Pilot nicht gefunden.</div>
      <button className="btn btn-outline-secondary" onClick={() => navigate(-1)}>Zurück</button>
    </div>
  );

  const isOwnProfile = userDetails?.userid === userid;

  return (
    <div className="container" style={{ padding: '40px' }}>
      {/* Header */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <button className="btn btn-link p-0" style={{ color: '#687088', fontWeight: 600, textDecoration: 'none', fontSize: '14px' }}
          onClick={() => navigate(-1)}>
          ← Zurück
        </button>
        <UserBadge />
      </div>

      <div className="row g-4">
        {/* Left column: profile card */}
        <div className="col-lg-4">
          <div className="card shadow-sm" style={{ borderRadius: '12px' }}>
            <div className="card-body p-4 text-center">
              {pilot.logo ? (
                <img src={`${UPLOADS_BASE_URL}${pilot.logo}`} alt={pilot.name}
                  style={{ width: '88px', height: '88px', borderRadius: '50%', objectFit: 'cover', border: '3px solid #e0e4ef', marginBottom: '16px' }} />
              ) : (
                <div style={{ ...getAvatarStyle(pilot.name, 88), margin: '0 auto 16px' }}>
                  {getInitials(pilot.name)}
                </div>
              )}
              <h4 style={{ fontWeight: 700, color: '#687088', marginBottom: '4px' }}>{pilot.name}</h4>
              {pilot.organization && (
                <p style={{ color: '#a0afc0', fontSize: '14px', marginBottom: '4px' }}>{pilot.organization}</p>
              )}
              {pilot.userRole && (
                <span style={{
                  background: '#e8f4fd', color: '#0d6efd', fontSize: '12px', fontWeight: 600,
                  padding: '3px 10px', borderRadius: '20px', display: 'inline-block', marginBottom: '20px',
                }}>
                  {ROLE_LABELS[pilot.userRole] || pilot.userRole}
                </span>
              )}

              {/* Stats */}
              <div style={{ background: '#f8f9fa', borderRadius: '8px', padding: '12px', marginBottom: '20px' }}>
                <div style={{ fontWeight: 700, fontSize: '24px', color: '#0d6efd' }}>{flights.length}</div>
                <div style={{ color: '#a0afc0', fontSize: '13px' }}>öffentliche Flüge</div>
              </div>

              {/* Friendship button */}
              {!isOwnProfile && (
                <div>
                  {friendStatus.status === 'none' && (
                    <button className="btn btn-primary w-100" style={{ fontWeight: 700, fontSize: '13px' }}
                      disabled={actionLoading} onClick={() => handleFriendAction('request')}>
                      Freundschaftsanfrage senden
                    </button>
                  )}
                  {friendStatus.status === 'pending_sent' && (
                    <button className="btn btn-outline-secondary w-100" disabled style={{ fontSize: '13px' }}>
                      Anfrage gesendet
                    </button>
                  )}
                  {friendStatus.status === 'pending_received' && (
                    <div className="d-flex gap-2">
                      <button className="btn btn-primary flex-fill" style={{ fontWeight: 700, fontSize: '13px' }}
                        disabled={actionLoading} onClick={() => handleFriendAction('accept')}>
                        Annehmen
                      </button>
                      <button className="btn btn-outline-secondary flex-fill" style={{ fontSize: '13px' }}
                        disabled={actionLoading} onClick={() => handleFriendAction('decline')}>
                        Ablehnen
                      </button>
                    </div>
                  )}
                  {friendStatus.status === 'accepted' && (
                    <div>
                      <div style={{ color: '#198754', fontWeight: 600, fontSize: '13px', marginBottom: '8px' }}>
                        ✓ Befreundet
                      </div>
                      <button className="btn btn-link p-0" style={{ color: '#a0afc0', fontSize: '12px' }}
                        disabled={actionLoading} onClick={() => handleFriendAction('remove')}>
                        Freundschaft beenden
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right column: public flights */}
        <div className="col-lg-8">
          <div className="card shadow-sm" style={{ borderRadius: '12px' }}>
            <div className="card-body p-4">
              <h5 style={{ fontWeight: 700, color: '#687088', marginBottom: '16px' }}>Öffentliche Flüge</h5>
              {flights.length === 0 ? (
                <p style={{ color: '#a0afc0', fontSize: '14px', margin: 0 }}>Keine öffentlichen Flüge vorhanden.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table" style={{ color: '#687088' }}>
                    <thead>
                      <tr>
                        <th>Datum</th>
                        <th>Gerät</th>
                        <th>Kennung</th>
                        <th>Dauer</th>
                        <th>Region</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {flights.map(f => (
                        <tr key={f._id} style={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/all-flights/${f._id}`)}>
                          <td>{formatDate(f.dateStart)}</td>
                          <td>{AIRCRAFT_TYPE_LABELS[f.aircraftid?.type || ''] || f.aircraftid?.type || '-'}</td>
                          <td style={{ fontWeight: 600 }}>{f.aircraftid?.identification || '-'}</td>
                          <td>{formatDuration(f.duration)}</td>
                          <td>{f.region || '-'}</td>
                          <td>
                            <span style={{ color: '#0d6efd', fontSize: '13px', fontWeight: 600 }}>Details</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PilotProfile;
